import { Check, X } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";

export default function PackageComparisonTable({ packages }) {
  const { t } = useLanguage();

  // Hardcoded core features to display in the table (ensures stable UI)
  const coreFeatures = [
    { key: "location", label: "Standort & Anreise" },
    { key: "chat", label: "Echtzeit-Chat" },
    { key: "guestbook", label: "Digitales Gästebuch" },
    { key: "wishlist", label: "Geschenk-Wünsche" },
    { key: "photo_upload", label: "Foto-Upload" },
    { key: "video_upload", label: "Video-Upload" },
    { key: "schedule", label: "Zeitplan & Programm" },
    { key: "timeline", label: "Event-Timeline" },
    { key: "seating_plan", label: "Sitzplaner" },
    { key: "tasks", label: "Aufgaben-Liste" },
    { key: "dresscode", label: "Dresscode-Info" },
  ];

  if (!packages || packages.length === 0) {
    return null;
  }

  return (
    <Card className="w-full border-none shadow-xl bg-white/50 backdrop-blur-sm overflow-hidden rounded-[2rem]">
      <CardHeader className="text-center pb-8 pt-10">
        <CardTitle className="text-3xl font-serif">{t('pricing.comparison')}</CardTitle>
        <CardDescription className="text-muted-foreground/60">
          Alle Details auf einen Blick
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-border/10">
                <th className="text-left p-6 font-bold text-foreground min-w-[200px] bg-secondary/5">
                  Features
                </th>
                {packages.map((pkg) => (
                  <th
                    key={pkg.id}
                    className="text-center p-6 font-bold text-foreground min-w-[140px] bg-secondary/5"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-xl font-black tracking-tight">{pkg.name}</span>
                      {pkg.id === 'premium' && (
                        <span className="text-[10px] uppercase tracking-[0.2em] bg-accent text-accent-foreground px-3 py-1 rounded-full font-black animate-pulse">
                          {t('pricing.recommended')}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {coreFeatures.map((feature, idx) => (
                <tr
                  key={feature.key}
                  className={`border-b border-border/5 border-dashed hover:bg-secondary/10 transition-colors ${idx % 2 === 0 ? 'bg-secondary/5' : ''}`}
                >
                  <td className="p-5 text-sm font-bold text-foreground/70">
                    {feature.label}
                  </td>
                  {packages.map((pkg) => {
                    // Check if package has feature (supports both array and object format)
                    let hasFeature = false;
                    const features = pkg.features || [];
                    if (Array.isArray(features)) {
                      hasFeature = features.includes(feature.key);
                    } else if (features && typeof features === 'object') {
                      hasFeature = features[feature.key] === true;
                    }
                    return (
                      <td key={`${pkg.id}-${feature.key}`} className="p-5 text-center">
                        {hasFeature ? (
                          <div className="w-6 h-6 bg-green-500/10 rounded-full flex items-center justify-center mx-auto">
                            <Check className="w-4 h-4 text-green-600" />
                          </div>
                        ) : (
                          <X className="w-4 h-4 text-muted-foreground/10 mx-auto" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* Limits */}
              <tr className="border-b border-border/5 bg-secondary/10">
                <td className="p-5 text-sm font-bold text-foreground">
                  Events
                </td>
                {packages.map((pkg) => (
                  <td key={`${pkg.id}-events`} className="p-5 text-center text-sm font-black text-primary">
                    {pkg.max_events === -1 || !pkg.max_events ? (
                      <span className="text-2xl">∞</span>
                    ) : (
                      pkg.max_events
                    )}
                  </td>
                ))}
              </tr>

              <tr className="border-border/5">
                <td className="p-5 text-sm font-bold text-foreground">
                  Gäste pro Event
                </td>
                {packages.map((pkg) => (
                  <td key={`${pkg.id}-guests`} className="p-5 text-center text-sm font-black text-primary">
                    {pkg.max_guests === -1 || !pkg.max_guests ? (
                      <span className="text-2xl">∞</span>
                    ) : (
                      pkg.max_guests
                    )}
                  </td>
                ))}
              </tr>

              <tr className="bg-primary/5">
                <td className="p-6 text-sm font-black uppercase tracking-widest text-primary">
                  {t('pricing.price')}
                </td>
                {packages.map((pkg) => (
                  <td key={`${pkg.id}-price`} className="p-6 text-center">
                    <div className="font-black text-xl text-primary flex flex-col items-center">
                      {pkg.price === 0 ? (
                        <span className="text-sm uppercase tracking-widest opacity-50">Kostenlos</span>
                      ) : (
                        <>
                          <span className="tracking-tighter">€{pkg.price}</span>
                          <span className="text-[10px] uppercase tracking-widest opacity-50 mt-1 font-bold">
                            {pkg.is_subscription ? t('pricing.perMonth') : 'Einmalig'}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
